import { apiClient } from './api';
import { publicBookingApiParamsFromClubRouteKey } from '../utils/club-route-key';
import { bookingTebox } from '../styles/tebox-booking';
import { FONT_OPTIONS, normalizeBookingBrandFontName } from '../../../web/src/components/BrandSettings/FontSelector';
import { GOOGLE_SANS_FLEX_FONT_NAME } from '../../../web/src/utils/fontFamily';

export interface BrandSettings {
  id?: string;
  clubId: string;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  buttonTextColor: string;
  primaryTextColor?: string;
  headingTextBold?: boolean;
  headingTextItalic?: boolean;
  headerColor: string;
  borderColor: string;
  borderRadius?: string;
  fontFamily: string;
  customCss?: string;
}

/** Full CSS `font-family` stack for a brand font name (matches `--brand-font` when settings are applied). */
export const getFontFamilyCSS = (fontFamily: string): string => {
  const normalized = normalizeBookingBrandFontName(fontFamily);
  if (!normalized) {
    return `'${GOOGLE_SANS_FLEX_FONT_NAME}', sans-serif`;
  }
  /** Already a CSS variable — do not quote as a literal font name (would ignore `--brand-font`). */
  if (normalized.startsWith('var(')) {
    return normalized;
  }
  const fontOption = FONT_OPTIONS.find((f) => f.value === normalized);
  if (fontOption) {
    return `'${normalized}', ${fontOption.category}`;
  }
  return `'${normalized}', serif`;
};

const loadGoogleFont = (fontFamily: string): Promise<void> => {
  const normalized = normalizeBookingBrandFontName(fontFamily);
  if (normalized === GOOGLE_SANS_FLEX_FONT_NAME) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const fontOption = FONT_OPTIONS.find((f) => f.value === normalized);
    if (!fontOption?.googleFont) {
      console.warn(`🎨 Font ${normalized} not found in Google Fonts mapping`);
      resolve();
      return;
    }

    const fontId = `google-font-${normalized.replace(/\s+/g, '-').toLowerCase()}`;

    const existingLink = document.getElementById(fontId);
    if (existingLink) {
      existingLink.remove();
    }

    const link = document.createElement('link');
    link.id = fontId;
    link.rel = 'stylesheet';
    link.href = fontOption.googleFont.includes(':')
      ? `https://fonts.googleapis.com/css2?family=${fontOption.googleFont}&display=swap`
      : `https://fonts.googleapis.com/css2?family=${fontOption.googleFont}:wght@300;400;500;600;700&display=swap`;

    link.onload = () => {
      if (document.fonts?.ready) {
        document.fonts.ready
          .then(() => setTimeout(resolve, 200))
          .catch(() => setTimeout(resolve, 500));
      } else {
        setTimeout(resolve, 500);
      }
    };

    link.onerror = () => {
      console.error(`❌ Failed to load Google Font: ${normalized}`);
      resolve();
    };

    document.head.appendChild(link);
  });
};

const BRAND_SETTINGS_CACHE_KEY = 'booking-brand-settings';

const getCachedBrandSettings = (clubIdOrNumber: string): BrandSettings | null => {
  try {
    const key = `${BRAND_SETTINGS_CACHE_KEY}-${clubIdOrNumber}`;
    const cached = localStorage.getItem(key);
    if (cached) {
      const parsed = JSON.parse(cached) as BrandSettings;
      const merged = { ...defaultBrandSettings, ...parsed };
      merged.fontFamily = normalizeBookingBrandFontName(merged.fontFamily);
      return merged;
    }
  } catch {
    /* ignore */
  }
  return null;
};

const cacheBrandSettings = (clubIdOrNumber: string, settings: BrandSettings): void => {
  try {
    const key = `${BRAND_SETTINGS_CACHE_KEY}-${clubIdOrNumber}`;
    localStorage.setItem(key, JSON.stringify(settings));
  } catch {
    /* ignore */
  }
};

/** Apply CSS variables synchronously (no font loading). Use for instant apply from cache. */
const applyBrandSettingsSync = (settings: BrandSettings): void => {
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', settings.primaryColor);
  root.style.setProperty('--brand-secondary', settings.secondaryColor);
  root.style.setProperty('--brand-accent', settings.accentColor);
  root.style.setProperty('--brand-background', settings.backgroundColor);
  root.style.setProperty('--brand-text', settings.textColor);
  root.style.setProperty('--brand-button', settings.buttonColor);
  root.style.setProperty('--brand-button-text', settings.buttonTextColor);
  root.style.setProperty('--brand-primary-text', settings.primaryTextColor || settings.buttonTextColor || '#FFFFFF');
  root.style.setProperty('--brand-heading-text-weight', settings.headingTextBold ? '700' : '500');
  root.style.setProperty('--brand-heading-text-style', settings.headingTextItalic ? 'italic' : 'normal');
  root.style.setProperty('--brand-header', settings.headerColor);
  root.style.setProperty('--brand-border', settings.borderColor);
  root.style.setProperty('--brand-border-radius', settings.borderRadius ?? '6px');
  const fontCSS = getFontFamilyCSS(settings.fontFamily);
  root.style.setProperty('--brand-font', fontCSS);
};

const defaultBrandSettings: BrandSettings = {
  clubId: '',
  primaryColor: '#000000',
  secondaryColor: bookingTebox.colors.textSecondary,
  accentColor: '#000000',
  backgroundColor: '#FFFFFF',
  /** TEBOX `typography.body` / `colors.text.primary` */
  textColor: '#021B1B',
  buttonColor: '#000000',
  buttonTextColor: '#FFFFFF',
  primaryTextColor: '#FFFFFF',
  headingTextBold: false,
  headingTextItalic: false,
  headerColor: bookingTebox.colors.bgPrimary,
  borderColor: bookingTebox.colors.border,
  borderRadius: '6px',
  fontFamily: GOOGLE_SANS_FLEX_FONT_NAME,
};

export const brandSettingsService = {
  // Get brand settings for a club (public endpoint)
  getByClubId: async (clubRouteKey: string): Promise<BrandSettings> => {
    try {
      const params = publicBookingApiParamsFromClubRouteKey(clubRouteKey);
      const url = `/brand-settings/public?${params.toString()}`;
      console.log('🎨 Fetching brand settings from:', url);
      const response = await apiClient.get<BrandSettings>(url);
      console.log('🎨 Raw API response:', response);

      const merged = { ...defaultBrandSettings, ...response };
      merged.fontFamily = normalizeBookingBrandFontName(merged.fontFamily);
      console.log('🎨 Merged brand settings:', merged);
      cacheBrandSettings(clubRouteKey, merged);
      return merged;
    } catch (error) {
      console.error(`❌ Failed to fetch brand settings:`, error);
      console.warn(`Using default brand settings`);
      return defaultBrandSettings;
    }
  },

  getCachedBrandSettings,
  applyBrandSettingsSync,

  // Apply brand settings to the document (loads font, then applies)
  applyBrandSettings: async (settings: BrandSettings): Promise<void> => {
    applyBrandSettingsSync(settings);
    console.log('🎨 Applying brand settings (font loading):', {
      fontFamily: settings.fontFamily,
      primaryColor: settings.primaryColor,
      buttonColor: settings.buttonColor,
    });

    console.log('🎨 Loading font:', settings.fontFamily);
    await loadGoogleFont(settings.fontFamily);
    console.log('🎨 Font loading complete:', settings.fontFamily);

    const fontCSS = getFontFamilyCSS(settings.fontFamily);
    const body = document.body;
    if (body) {
      body.style.fontFamily = fontCSS;
      void body.offsetHeight;
      body.style.fontFamily = '';
    }

    if (settings.customCss) {
      const existingStyle = document.getElementById('custom-brand-css');
      if (existingStyle) {
        existingStyle.remove();
      }

      const style = document.createElement('style');
      style.id = 'custom-brand-css';
      style.textContent = settings.customCss;
      document.head.appendChild(style);
    }

    console.log('🎨 Brand settings applied successfully');
  },
};
